import React from "react";
import TextInput from "../Common/TextInput";
import UserRolesTable from "../UserRolesTable";
import Resources from "../../types/Resources";
import Role from "../../types/Role";
import { UserFormState } from "../../types/forms/UserFormState";
import Multiselect from "multiselect-react-dropdown";
import { DepartmentRoleOptions } from "../../types/DepartmentRoleOptions";
import { CompanyOption } from "../../types/CompanyOption";
import { CompanyDepartmentOption } from "../../types/CompanyDepartmentOption";
import Button from "../Common/Button";

type Props = {
    resources: Resources;
    formState: UserFormState;
    showNewCompanyInputs: boolean;
    showNewCompanyDepartmentInput: boolean;
    departmentRoleOptions: DepartmentRoleOptions[];
    showNewUserRoleRow: boolean;
    companyOptions: CompanyOption[];
    companyDepartmentOptions: CompanyDepartmentOption[];
    submitButtonText: string;
    onFirstNameChange: (newValue: string) => void;
    onLastNameChange: (newValue: string) => void;
    onEmailChange: (newValue: string) => void;
    onPhoneNumberChange: (newValue: string) => void;
    onPasswordChange?: (newValue: string) => void;
    onConfirmationPasswordChange?: (newValue: string) => void;
    onReceivesSmsChange: (newValue: boolean) => void;
    onNewCompanyNameChange: (newValue: string) => void;
    onShowNewCompanyInputs: () => void;
    onHideNewCompanyInputs: () => void;
    onShowNewCompanyDepartmentInput: () => void;
    onHideNewCompanyDepartmentInput: () => void;
    onNewCompanyDepartmentNameChange: (newValue: string) => void;
    onRoleAdded: (role: Role) => void;
    onRoleRemoved: (role: Role) => void;
    onShowNewUserRoleRow: () => void;
    onHideNewUserRoleRow: () => void;
    onCompanySearch: (searchText: string) => void;
    onCompanySelected: (selectedList: CompanyOption[], selectedItem: CompanyOption) => void;
    onCompanyRemoved: (selectedList: CompanyOption[], removedItem: CompanyOption) => void;
    onCompanyDepartmentSelected: (selectedList: CompanyDepartmentOption[], selectedItem: CompanyDepartmentOption) => void;
    onCompanyDepartmentRemoved: (selectedList: CompanyDepartmentOption[], removedItem: CompanyDepartmentOption) => void;
    onSubmit: (e: React.FormEvent) => void;
    onChangePasswordClick?: () => void;
};

function UserForm(props: Props) {
    const selectedCompany = props.companyOptions.find((c) => c.id === props.formState.companyId.value);
    const selectedCompanyDepartment = props.companyDepartmentOptions.find(
        (d) => d.id === props.formState.companyDepartmentId.value
    );

    const showChangePasswordButton = !!props.onChangePasswordClick;
    const showNewPasswordFields = !!props.onPasswordChange && !!props.onConfirmationPasswordChange;

    return (
        <div className="container mt-3">
            {showChangePasswordButton ? (
                <div className="row mb-3">
                    <div className="col-md-2">
                        <Button variant="secondary" className="w-100" onClick={props.onChangePasswordClick}>
                            {props.resources.get("ChangePassword")}
                        </Button>
                    </div>
                </div>
            ) : null}
            <form onSubmit={props.onSubmit}>
                <div className="row gy-3">
                    <TextInput
                        inputId="first-name-input"
                        labelText={props.resources.get("FirstName")}
                        onChange={props.onFirstNameChange}
                        readOnly={false}
                        value={props.formState.firstName.value}
                        boldLabel={false}
                        containerClassName="col-md-6"
                        errorMessage={props.formState.firstName.errorMessage}
                    />

                    <TextInput
                        inputId="last-name-input"
                        labelText={props.resources.get("LastName")}
                        onChange={props.onLastNameChange}
                        readOnly={false}
                        value={props.formState.lastName.value}
                        boldLabel={false}
                        containerClassName="col-md-6"
                        errorMessage={props.formState.lastName.errorMessage}
                    />

                    <TextInput
                        inputId="email-input"
                        labelText={props.resources.get("EmailAddress")}
                        onChange={props.onEmailChange}
                        readOnly={false}
                        value={props.formState.email.value}
                        boldLabel={false}
                        containerClassName="col-md-6"
                        errorMessage={props.formState.email.errorMessage}
                    />

                    <TextInput
                        inputId="phone-number-input"
                        labelText={props.resources.get("PhoneNumber")}
                        onChange={props.onPhoneNumberChange}
                        readOnly={false}
                        value={props.formState.phoneNumber.value}
                        boldLabel={false}
                        containerClassName="col-md-6"
                        errorMessage={props.formState.phoneNumber.errorMessage}
                    />

                    {showNewPasswordFields ? (
                        <>
                            <TextInput
                                inputId="password-input"
                                labelText={props.resources.get("Password")}
                                onChange={props.onPasswordChange!}
                                readOnly={false}
                                value={props.formState.password.value}
                                boldLabel={false}
                                containerClassName="col-md-6"
                                errorMessage={props.formState.password.errorMessage}
                                type="password"
                            />
                            
                            <TextInput
                                inputId="confirmation-password-input"
                                labelText={props.resources.get("ConfirmPassword")}
                                onChange={props.onConfirmationPasswordChange!}
                                readOnly={false}
                                value={props.formState.confirmationPassword.value}
                                boldLabel={false}
                                containerClassName="col-md-6"
                                errorMessage={props.formState.confirmationPassword.errorMessage}
                                type="password"
                            />
                        </>
                    ) : null}

                    {props.showNewCompanyInputs ? (
                        <>
                            <div className="col-md-6">
                                <TextInput
                                    inputId="new-company-name-input"
                                    labelText={props.resources.get("NewCompany")}
                                    onChange={props.onNewCompanyNameChange}
                                    readOnly={false}
                                    value={props.formState.newCompanyName.value}
                                    boldLabel={false}
                                    errorMessage={props.formState.newCompanyName.errorMessage}
                                />
                                <Button className="px-0" variant="link" onClick={props.onHideNewCompanyInputs}>
                                    {props.resources.get("SelectExistingCompany")}
                                </Button>
                            </div>
                            <div className="col-md-6">
                                <TextInput
                                    inputId="new-company-department-name-input"
                                    labelText={`${props.resources.get("NewCompanyDepartment")} (${props.resources.get("Optional")?.toLowerCase()})`}
                                    onChange={props.onNewCompanyDepartmentNameChange}
                                    readOnly={false}
                                    value={props.formState.newCompanyDepartmentName.value}
                                    boldLabel={false}
                                    errorMessage={props.formState.newCompanyDepartmentName.errorMessage}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="col-md-6">
                                <label className="form-label" htmlFor="company-select_input">
                                    {props.resources.get("Company")}
                                </label>
                                <Multiselect
                                    id="company-select"
                                    options={props.companyOptions}
                                    onSearch={props.onCompanySearch}
                                    selectionLimit={1}
                                    displayValue="name"
                                    selectedValues={selectedCompany ? [selectedCompany] : []}
                                    onSelect={props.onCompanySelected}
                                    onRemove={props.onCompanyRemoved}
                                    hidePlaceholder={!!props.formState.companyId.value}
                                    closeOnSelect={true}
                                    emptyRecordMsg={
                                        !props.formState.companyId.value ? props.resources.get("NoCompanies") : ""
                                    }
                                    placeholder={props.resources.get("CompanyPlaceholder")}
                                    style={{ inputField: { width: "100%", display: selectedCompany ? "none" : undefined } }}
                                />
                                <span className="text-danger">{props.formState.companyId.errorMessage}</span>
                                {!selectedCompany ? (
                                    <Button className="px-0" variant="link" onClick={props.onShowNewCompanyInputs}>
                                        {props.resources.get("CreateNewCompany")}
                                    </Button>
                                ) : null}
                            </div>
                            {props.showNewCompanyDepartmentInput ? (
                                <div className="col-md-6">
                                    <TextInput
                                        inputId="new-company-department-name-input"
                                        labelText={props.resources.get("NewCompanyDepartment")}
                                        onChange={props.onNewCompanyDepartmentNameChange}
                                        readOnly={false}
                                        value={props.formState.newCompanyDepartmentName.value}
                                        boldLabel={false}
                                        errorMessage={props.formState.newCompanyDepartmentName.errorMessage}
                                    />
                                    <Button
                                        className="px-0"
                                        variant="link"
                                        onClick={props.onHideNewCompanyDepartmentInput}
                                    >
                                        {props.resources.get("SelectExistingCompanyDepartment")}
                                    </Button>
                                </div>
                            ) : (
                                <div className="col-md-6">
                                    <label className="form-label" htmlFor="company-department-select_input">
                                        {props.resources.get("CompanyDepartment")} (
                                        {props.resources.get("Optional")?.toLowerCase()})
                                    </label>
                                    <Multiselect
                                        id="company-department-select"
                                        displayValue="name"
                                        options={props.companyDepartmentOptions}
                                        selectionLimit={1}
                                        hidePlaceholder={!!props.formState.companyDepartmentId.value}
                                        closeOnSelect={true}
                                        onRemove={props.onCompanyDepartmentRemoved}
                                        onSelect={props.onCompanyDepartmentSelected}
                                        emptyRecordMsg={
                                            !props.formState.companyDepartmentId.value
                                                ? props.resources.get("NoCompanyDepartments")
                                                : ""
                                        }
                                        placeholder={props.resources.get("CompanyDepartmentPlaceholder")}
                                        selectedValues={selectedCompanyDepartment ? [selectedCompanyDepartment] : []}
                                        style={{ inputField: { width: "100%", display: selectedCompanyDepartment ? "none" : undefined } }}
                                    />
                                    <span className="text-danger">
                                        {props.formState.companyDepartmentId.errorMessage}
                                    </span>
                                    {selectedCompany && !selectedCompanyDepartment ? (
                                        <Button
                                            className="px-0 text-start"
                                            variant="link"
                                            onClick={props.onShowNewCompanyDepartmentInput}
                                        >
                                            {props.resources.get("CreateNewCompanyDepartment")}{" "}
                                            <span className="text-nowrap">{selectedCompany?.name}</span>
                                        </Button>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}

                    <div className="col-12">
                        <div className="form-check form-switch">
                            <label className="form-check-label fw-bold" htmlFor="receives-email-input">
                                {props.resources.get("ContactedByEmail")}
                            </label>
                            <input
                                id="receives-email-input"
                                className="form-check-input"
                                type="checkbox"
                                checked={true}
                                disabled
                            />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="form-check form-switch">
                            <label className="form-check-label fw-bold" htmlFor="receives-sms-input">
                                {props.resources.get("ContactedBySms")}
                            </label>
                            <input
                                id="receives-sms-input"
                                className="form-check-input"
                                type="checkbox"
                                checked={props.formState.receivesSms.value}
                                onChange={(e) => props.onReceivesSmsChange(e.target.checked)}
                            />
                        </div>
                    </div>

                    <UserRolesTable
                        resources={props.resources}
                        departmentRoleOptions={props.departmentRoleOptions}
                        roles={props.formState.roles.value}
                        defaultShowNewUserRoleRow={true}
                        onRoleAdded={props.onRoleAdded}
                        onRoleRemoved={props.onRoleRemoved}
                        showNewUserRoleRow={props.showNewUserRoleRow}
                        onShowNewUserRoleRow={props.onShowNewUserRoleRow}
                        onHideNewUserRoleRow={props.onHideNewUserRoleRow}
                        errorMessage={props.formState.roles.errorMessage}
                    />

                    <div className="col-12">
                        <Button type="submit" variant="primary">
                            {props.submitButtonText}
                        </Button>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default UserForm;
